import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, EditableToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

const RICH_TEXT_CLASS =
  'font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_a]:text-[var(--vibe-color-primary)] [&_a]:underline [&_a:hover]:text-[var(--vibe-color-secondary)] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1';

export function AccessibilityStatement() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 italic">
            Commitment to Digital Accessibility
          </h2>
          <EditableToken path="accessibilitystatement.commitment" type="richtext" label="Commitment to Digital Accessibility">
            <RichTextContent
              path="accessibilitystatement.commitment"
              as="div"
              className={RICH_TEXT_CLASS}
            />
          </EditableToken>
        </div>
        <div className="mt-10">
          <h2 className="font-heading text-xl sm:text-2xl font-normal text-[var(--vibe-color-text)] mb-4 italic">
            We Are Here for You
          </h2>
          <EditableToken path="accessibilitystatement.here_for_you" type="richtext" label="We Are Here for You">
            <RichTextContent
              path="accessibilitystatement.here_for_you"
              as="div"
              className={RICH_TEXT_CLASS}
            />
          </EditableToken>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AccessibilityStatement',
  displayName: 'Accessibility Statement',
  tokens: {
    'accessibilitystatement.commitment': {
      label: 'Commitment to Digital Accessibility',
      type: 'richtext',
      defaultValue:
        '<p>We are dedicated to providing an equivalent digital experience for all website users, regardless of physical or cognitive ability. To uphold our commitment, we adhere to the Website Content Accessibility Guidelines (WCAG) 2.1, Level AA standards in the design, testing and development of our website experiences. The WCAG 2.1 Level AA standards are recognized and acknowledged as the international standard measure of success.</p><p>We make it easy to navigate our website by supporting assistive technologies such as screen readers, magnifiers, and voice recognition software. Our digital standards, design and development teams regularly collaborate to ensure we follow accessibility best practices, and consult with accessibility and usability specialists, and people with disabilities to make sure our sites function properly and that we continue to learn as technology expands. These efforts help ensure that our content is available to all, including those who rely on assistive technology.</p>',
    },
    'accessibilitystatement.here_for_you': {
      label: 'We Are Here for You',
      type: 'richtext',
      defaultValue:
        '<p>We are always learning. We consistently look for improvements in performance, speed, and accessibility. If you are having difficulty using this website because of a disability or have an issue to report, we are here to receive your feedback and provide you with access to all of the information and functionality on this website. Please contact us using the form on our <a href="/contact-us">Contact Us</a> page or by the email/phone number located on our website.</p>',
    },
  },
};

(AccessibilityStatement as any).contentSchema = contentSchema;

export default AccessibilityStatement;
